import React, { FormEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import range from 'lodash/range';
import { UseQueryResult } from 'react-query';
import { useKey } from 'react-use';
import { Button } from 'semantic-ui-react';
import { DataSearchResponse, Region } from '../../../../shared/api/api';
import { useDataSearch } from '../../../../shared/api/apiHooks';
import { addModifiers } from '../../../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../../shared/helpers/internationalization';
import {
  getBrowseDataFieldsPath,
  getDataFieldPath,
  getDataSearchResultPath,
  getDatasetsPath,
  getSingleDatasetPath,
} from '../../dataExplorerRouterPaths';
import { InputField } from '../../Filters/FilterFields/InputField';
import Skeleton from 'react-loading-skeleton';

import './MainDataSearch.scss';
import { isMobile } from '../../../../shared/helpers/responsive';
import { useDataExplorerTrackCustomEvent } from '../../Filters/dataExplorerAnalytics';
import { filterFields } from '../../Filters/FilterFields/dataExplorerFieldsConfig';
import { DataExplorerLink, useDataExplorerHistory } from '../../dataExplorerHistoryContext';

interface Props {
  result: UseQueryResult<DataSearchResponse, Error>;
  search: string;
  region: Region;
  delay: number;
}
const ROWS_COUNT = 5;
const SEARCH_FIELD_NAME = filterFields.search.name;

const isSearchEnabled = (query: string = '') => query.length > 0;

interface MainDataSearchProps {
  //todo: use better types
  debouncedQueryObject: any;
  values: any;
  flushDebounce: any;
  control: any;
  reset: any;
}

export const MainDataSearch = injectIntl(
  memo(({ intl, debouncedQueryObject, values, flushDebounce, control, reset }: InjectedIntlProps & MainDataSearchProps) => {
    const searchQueryDebounced = debouncedQueryObject[SEARCH_FIELD_NAME];
    const searchQueryImmediate = values[SEARCH_FIELD_NAME];
    const fetchingEnabled = isSearchEnabled(searchQueryDebounced);

    const [searchActive, setSearchActive] = useState(false);
    const searchButtonEnabled = isSearchEnabled(searchQueryImmediate);
    const history = useDataExplorerHistory();

    const showSearch = searchActive && fetchingEnabled;

    const result = useDataSearch({ queryObject: debouncedQueryObject, enabled: fetchingEnabled });

    const searchActiveRef = useRef(false);
    useEffect(() => {
      if (!searchActiveRef.current) {
        searchActiveRef.current = true;
        return;
      }

      setSearchActive(true);
    }, [debouncedQueryObject]);

    // flush debounced values immediately if input is empty
    useEffect(() => {
      if (!searchQueryImmediate) {
        flushDebounce();
      }
    }, [searchQueryImmediate, flushDebounce]);

    const searchContainerRef = useRef<HTMLFormElement>(null);

    const hideSearch = useCallback(() => {
      setSearchActive(false);
      (document.activeElement as HTMLElement | null)?.blur();
    }, []);

    useKey('Escape', hideSearch);

    // handle click outside of search
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
          hideSearch();
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [searchContainerRef]);

    const onInputFocus = useCallback(() => {
      setSearchActive(true);
    }, []);

    const onSubmit = (e: FormEvent) => {
      e.preventDefault();
      history.push(getDataSearchResultPath({ searchParam: searchQueryImmediate, tab: 'data-fields' }));
    };

    const trackEvent = useDataExplorerTrackCustomEvent();

    return (
      <form onSubmit={onSubmit} ref={searchContainerRef} className={addModifiers('data-search', searchActive ? ['active'] : [])}>
        <div className="data-search__input-wrapper">
          <InputField
            inputProps={{
              autoFocus: true,
              onFocus: onInputFocus,
              className: 'data-search__search-input',
              type: 'search',
              icon: 'search',
              placeholder: formatMessageHelper(
                intl,
                isMobile('mobile') ? 'dataExplorer.search.mobilePlaceholder' : 'dataExplorer.search.placeholder'
              ),
              iconPosition: 'left',
            }}
            controllerProps={{ control, name: SEARCH_FIELD_NAME }}
          />
          {searchButtonEnabled && (
            <button data-testid={'data-clear-button'} onClick={reset} type="button" className="data-search__clear-button"></button>
          )}
        </div>
        <Button
          data-testid={'data-search-button'}
          onClick={() => trackEvent('de_click_search')}
          type="submit"
          disabled={!searchButtonEnabled}
          className="data-search__button"
        >
          <FormattedMessageHelper id="dataExplorer.search.searchButton" />
        </Button>
        {showSearch && <MainDataSearchModal search={searchQueryImmediate} result={result} region={values.region} delay={values.delay} />}
      </form>
    );
  })
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MainDataSearchModal = ({ result, search }: Props) => {
  if (result.error) {
    return (
      <section className="data-search-result">
        <FormattedMessageHelper id="dataExplorer.search.error" />
      </section>
    );
  }

  if (result.isFetching) {
    return (
      <section className="data-search-result">
        <div className="data-search-result__section">
          <div className="data-search-result__links-mobile">
            <DataExplorerLink to={getBrowseDataFieldsPath({ searchParam: search })}>
              <span className="data-search-result__search-all">
                <FormattedMessageHelper id="dataExplorer.search.allFields" />
              </span>
            </DataExplorerLink>
            <DataExplorerLink to={getDatasetsPath({ queryParams: { search } })}>
              <span className="data-search-result__search-all">
                <FormattedMessageHelper id="dataExplorer.search.allDatasets" />
              </span>
            </DataExplorerLink>
          </div>
          <h3 className="data-search-result__title">
            <FormattedMessageHelper id="dataExplorer.search.fields" />
          </h3>
          <ul className="data-search-result__list">
            {range(ROWS_COUNT).map((_, i) => (
              <li key={`field-placeholder-${i}`} className={addModifiers('data-search-result__list-item', ['placeholder-large'])}>
                <Skeleton />
              </li>
            ))}
            <Skeleton className="data-search-result__skeleton-desktop" width={150} />
          </ul>
        </div>
        <div className="data-search-result__section">
          <h3 className="data-search-result__title">
            <FormattedMessageHelper id="dataExplorer.search.datasets" />
          </h3>
          <ul className="data-search-result__list">
            {range(ROWS_COUNT).map((_, i) => (
              <li key={`dataset-placeholder-${i}`} className={addModifiers('data-search-result__list-item', ['placeholder'])}>
                <Skeleton />
              </li>
            ))}
            <Skeleton className="data-search-result__skeleton-desktop" width={150} />
          </ul>
        </div>
      </section>
    );
  }

  if (result.isSuccess) {
    return (
      <section className="data-search-result">
        <div className="data-search-result__section">
          <div className="data-search-result__links-mobile">
            <DataExplorerLink to={getBrowseDataFieldsPath({ searchParam: search })}>
              <span className="data-search-result__search-all">
                <FormattedMessageHelper id="dataExplorer.search.allFields" />
              </span>
            </DataExplorerLink>
            <DataExplorerLink to={getDatasetsPath({ queryParams: { search } })}>
              <span className="data-search-result__search-all">
                <FormattedMessageHelper id="dataExplorer.search.allDatasets" />
              </span>
            </DataExplorerLink>
          </div>
          <h3 className="data-search-result__title">
            <FormattedMessageHelper id="dataExplorer.search.fields" />
          </h3>
          <ul className="data-search-result__list">
            {result.data.fields.length === 0 ? (
              <li className="data-search-result__list-item">
                <FormattedMessageHelper id="dataExplorer.search.noFields" />
              </li>
            ) : (
              result.data.fields.slice(0, ROWS_COUNT).map(field => (
                <li key={field.id} className="data-search-result__list-item">
                  <DataExplorerLink
                    to={getDataFieldPath({ datasetId: field.dataset.id, fieldId: field.id })}
                    className="data-search-result__item-name"
                  >
                    {field.description}
                  </DataExplorerLink>
                  <div className="data-search-result__item-source">
                    <FormattedMessageHelper
                      id="dataExplorer.search.fieldValue"
                      values={{
                        field: (
                          <DataExplorerLink
                            className="data-search-result__item-source"
                            to={getDataFieldPath({ datasetId: field.dataset.id, fieldId: field.id })}
                          >
                            {field.id}
                          </DataExplorerLink>
                        ),
                        dataset: (
                          <DataExplorerLink
                            className="data-search-result__item-source"
                            to={getSingleDatasetPath({ datasetId: field.dataset.id })}
                          >
                            {field.dataset.name}
                          </DataExplorerLink>
                        ),
                      }}
                    />
                  </div>
                </li>
              ))
            )}
          </ul>

          <DataExplorerLink className="data-search-result__link-desktop" to={getBrowseDataFieldsPath({ searchParam: search })}>
            <span className="data-search-result__search-all">
              <FormattedMessageHelper id="dataExplorer.search.allFields" />
            </span>
          </DataExplorerLink>
        </div>

        <div className="data-search-result__section">
          <h3 className="data-search-result__title">
            <FormattedMessageHelper id="dataExplorer.search.datasets" />
          </h3>
          <ul className="data-search-result__list">
            {result.data.datasets.length === 0 ? (
              <li className="data-search-result__list-item">
                <FormattedMessageHelper id="dataExplorer.search.noDatasets" />
              </li>
            ) : (
              result.data.datasets.slice(0, ROWS_COUNT).map(dataset => (
                <li key={dataset.id} className="data-search-result__list-item">
                  <DataExplorerLink to={getSingleDatasetPath({ datasetId: dataset.id })} className="data-search-result__item-name">
                    {dataset.name}
                  </DataExplorerLink>
                </li>
              ))
            )}
          </ul>
          <DataExplorerLink className="data-search-result__link-desktop" to={getDatasetsPath({ queryParams: { search } })}>
            <span className="data-search-result__search-all">
              <FormattedMessageHelper id="dataExplorer.search.allDatasets" />
            </span>
          </DataExplorerLink>
        </div>
      </section>
    );
  }

  return null;
};
